import type {
  AppPreferences,
  DartThrow,
  GameNotice,
  GameState,
  PlayerState,
  TeamState,
  TurnRecord,
} from "./types";

const ACTIVE_KEY = "bullseye.active.v1";
const HISTORY_KEY = "bullseye.history.v1";
const UNDO_KEY = "bullseye.undo.v1";
const PREFERENCES_KEY = "bullseye.preferences.v1";

export const DEFAULT_PREFERENCES: AppPreferences = {
  soundVolume: 0.7,
};

interface LegacyPlayer {
  id: string;
  name: string;
  score: number;
  legs: number;
  color: string;
}

interface LegacyDart extends Omit<DartThrow, "teamId"> {}
interface LegacyTurn extends Omit<TurnRecord, "teamId" | "darts"> {
  darts: LegacyDart[];
}

interface LegacyGame {
  version: 1;
  config: GameState["config"];
  players: LegacyPlayer[];
  activePlayer: number;
  startingPlayer: number;
  leg: number;
  round: number;
  currentTurnStart: number;
  currentTurn: LegacyDart[];
  turns: LegacyTurn[];
  status: GameState["status"];
  winnerId?: string;
  notice?: GameNotice;
  createdAt: number;
  updatedAt: number;
}

const teamIdForPlayer = (playerId: string) => `team-${playerId}`;

export function migrateGame(value: unknown): GameState | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<GameState> & Partial<LegacyGame>;

  if (
    candidate.version === 2 &&
    Array.isArray(candidate.teams) &&
    candidate.teams.length >= 2 &&
    Array.isArray(candidate.players) &&
    candidate.players.length >= 2 &&
    Array.isArray(candidate.turns) &&
    candidate.config &&
    typeof candidate.activePlayerId === "string"
  ) {
    return candidate as GameState;
  }

  if (
    candidate.version !== 1 ||
    !Array.isArray(candidate.players) ||
    candidate.players.length < 2 ||
    !Array.isArray(candidate.turns) ||
    !Array.isArray(candidate.currentTurn) ||
    !candidate.config
  ) {
    return null;
  }

  const legacy = candidate as LegacyGame;
  const players: PlayerState[] = legacy.players.map((player) => ({
    id: player.id,
    teamId: teamIdForPlayer(player.id),
    name: player.name,
    color: player.color,
  }));
  const teams: TeamState[] = legacy.players.map((player) => ({
    id: teamIdForPlayer(player.id),
    name: player.name,
    score: player.score,
    legs: player.legs,
    color: player.color,
    playerIds: [player.id],
  }));
  const withTeam = (dart: LegacyDart): DartThrow => ({
    ...dart,
    teamId: teamIdForPlayer(dart.playerId),
  });
  const turns: TurnRecord[] = legacy.turns.map((turn) => ({
    ...turn,
    teamId: teamIdForPlayer(turn.playerId),
    darts: turn.darts.map(withTeam),
  }));
  const activeTeam = Math.min(
    Math.max(0, legacy.activePlayer ?? 0),
    Math.max(0, teams.length - 1),
  );
  const activePlayerId = players[activeTeam]?.id ?? players[0].id;

  return {
    version: 2,
    mode: "individual",
    config: legacy.config,
    teams,
    players,
    activeTeam,
    startingTeam: Math.min(
      Math.max(0, legacy.startingPlayer ?? 0),
      Math.max(0, teams.length - 1),
    ),
    activeMemberByTeam: Object.fromEntries(teams.map((team) => [team.id, 0])),
    activePlayerId,
    leg: legacy.leg,
    round: legacy.round,
    currentTurnStart: legacy.currentTurnStart,
    currentTurn: legacy.currentTurn.map(withTeam),
    turns,
    status: legacy.status,
    winnerTeamId: legacy.winnerId ? teamIdForPlayer(legacy.winnerId) : undefined,
    checkoutPlayerId: legacy.winnerId,
    notice: legacy.notice,
    createdAt: legacy.createdAt,
    updatedAt: legacy.updatedAt,
  };
}

export function saveGame(state: GameState): void {
  try {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(state));
  } catch {
    // The active React state stays usable even when storage is unavailable.
  }
}

export function loadGame(): GameState | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    return migrateGame(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveUndoHistory(history: GameState[]): void {
  try {
    localStorage.setItem(UNDO_KEY, JSON.stringify(history.slice(-100)));
  } catch {
    // Undo remains available for the current session.
  }
}

export function loadUndoHistory(): GameState[] {
  try {
    const raw = localStorage.getItem(UNDO_KEY);
    if (!raw) return [];
    const history = JSON.parse(raw) as unknown[];
    return Array.isArray(history)
      ? history.map(migrateGame).filter((item): item is GameState => Boolean(item)).slice(-100)
      : [];
  } catch {
    return [];
  }
}

export function savePreferences(preferences: AppPreferences): void {
  try {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // Preferences fall back to the in-memory React state.
  }
}

export function loadPreferences(): AppPreferences {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<AppPreferences>;
    return {
      soundVolume:
        typeof parsed.soundVolume === "number"
          ? Math.min(1, Math.max(0, parsed.soundVolume))
          : DEFAULT_PREFERENCES.soundVolume,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function archiveGame(state: GameState): void {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const history = raw ? (JSON.parse(raw) as unknown[]) : [];
    const valid = history
      .map(migrateGame)
      .filter((item): item is GameState => Boolean(item));
    if (!valid.some((item) => item.createdAt === state.createdAt)) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify([state, ...valid].slice(0, 20)));
    }
  } catch {
    // History is optional and must never block a live match.
  }
}

export function exportGame(state: GameState): void {
  const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `bullseye-partita-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
